import { useState, useCallback } from 'react'
import { loadStripe } from '@stripe/stripe-js'
import { Elements } from '@stripe/react-stripe-js'
import { CreditCard, RefreshCw, CheckCircle, RotateCcw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import api from '../services/api'
import PaymentForm from './PaymentForm'
import type { Invoice } from '../types'

interface Props {
  invoice: Invoice
  onPaid?: () => void
}

/**
 * "Payer" button rendered on an unpaid invoice.
 * On click: loads Stripe.js, creates a PaymentIntent, opens the PaymentForm modal.
 */
export default function InvoicePayButton({ invoice, onPaid }: Props) {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [paid, setPaid] = useState(false)
  const [stripePromise, setStripePromise] = useState<ReturnType<typeof loadStripe> | null>(null)
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [showModal, setShowModal] = useState(false)

  const handleClick = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await api.createPaymentIntent(invoice.id)
      const stripe = loadStripe(result.publishable_key)
      setStripePromise(stripe)
      setClientSecret(result.client_secret)
      setShowModal(true)
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        t('payment.error', 'Erreur lors de la création du paiement.')
      setError(msg)
    } finally {
      setLoading(false)
    }
  }, [invoice.id, t])

  const handleSuccess = useCallback(() => {
    setShowModal(false)
    setPaid(true)
    onPaid?.()
  }, [onPaid])

  if (paid) {
    return (
      <span className="inline-flex items-center gap-1 text-sm text-green-600 font-medium">
        <CheckCircle size={15} />
        {t('payment.paid', 'Payé')}
      </span>
    )
  }

  return (
    <>
      <button
        onClick={handleClick}
        disabled={loading}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
      >
        {loading ? <RefreshCw size={14} className="animate-spin" /> : <CreditCard size={14} />}
        {t('payment.pay', 'Payer')}
      </button>

      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}

      {/* Payment modal */}
      {showModal && stripePromise && clientSecret && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md p-6 relative">
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <RotateCcw size={18} />
            </button>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
              {t('payment.modalTitle', 'Paiement sécurisé')}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
              {t('payment.modalDesc', 'Facture')} {invoice.ref} —{' '}
              {Number(invoice.total_incl_tax ?? 0).toLocaleString('fr-FR', {
                style: 'currency',
                currency: 'EUR',
              })}
            </p>
            <Elements stripe={stripePromise} options={{ clientSecret }}>
              <PaymentForm
                clientSecret={clientSecret}
                onSuccess={handleSuccess}
                onCancel={() => setShowModal(false)}
              />
            </Elements>
          </div>
        </div>
      )}
    </>
  )
}
